import React, { useState, useEffect } from 'react';
import { gradeApi } from '../../../api.js';
import { useAuth } from '../../../auth/AuthContext';
import {
  Loader,
  RefreshCw,
  LogOut,
  ChevronRight,
  Calendar,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import './GoogleClassroomImportWizard.css';

const GoogleClassroomImportWizard = ({
  assignmentId,
  onImportComplete,
  onCancel,
  isUploading,
  setIsUploading
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: auth, 2: course selection, 3: assignment selection, 4: confirmation
  const [googleAuthStatus, setGoogleAuthStatus] = useState({
    authenticated: false,
    loading: true
  });
  const [googleCourses, setGoogleCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const [error, setError] = useState('');

  // Check Google auth status
  const checkGoogleAuthStatus = async () => {
    try {
      setGoogleAuthStatus(prev => ({ ...prev, loading: true }));
      const response = await gradeApi.get({ path: '/api/v2/google-classroom/auth/status' });

      const authStatus = {
        authenticated: response?.data?.isAuthenticated === true,
        loading: false
      };
      setGoogleAuthStatus(authStatus);

      if (authStatus.authenticated) {
        setStep(2);
        await fetchGoogleCourses();
      }

      return authStatus;
    } catch (error) {
      console.error('Error checking Google auth status:', error);
      setGoogleAuthStatus({ authenticated: false, loading: false });
      return { authenticated: false, loading: false };
    }
  };

  // Authenticate with Google
  const authenticateWithGoogle = async () => {
    try {
      setGoogleAuthStatus(prev => ({ ...prev, loading: true }));

      // Load Google Identity Services
      await loadGoogleAuthScript();

      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope:
          'https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.rosters.readonly https://www.googleapis.com/auth/classroom.coursework.students https://www.googleapis.com/auth/classroom.coursework.students https://www.googleapis.com/auth/drive.readonly',
        access_type: 'offline',
        prompt: 'consent',
        callback: async tokenResponse => {
          if (tokenResponse && tokenResponse.access_token) {
            try {
              await gradeApi.post({
                path: '/api/v2/google-classroom/auth',
                body: {
                  access_token: tokenResponse.access_token,
                  token_type: tokenResponse.token_type || 'Bearer',
                  expires_in: tokenResponse.expires_in || 3600,
                  scope: tokenResponse.scope || '',
                  refresh_token: tokenResponse.refresh_token || null
                }
              });

              setGoogleAuthStatus({ authenticated: true, loading: false });
              setStep(2);
              await fetchGoogleCourses();
            } catch (error) {
              console.error('Error sending token to backend:', error);
              setError('Failed to authenticate with Google Classroom');
              setGoogleAuthStatus({ authenticated: false, loading: false });
            }
          }
        }
      });

      tokenClient.requestAccessToken({ prompt: 'consent' });
    } catch (error) {
      console.error('Error authenticating with Google:', error);
      setError('Failed to start Google authentication');
      setGoogleAuthStatus({ authenticated: false, loading: false });
    }
  };

  // Load Google Auth Script
  const loadGoogleAuthScript = () => {
    if (window.google && window.google.accounts) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => resolve();
      script.async = true;
      document.head.appendChild(script);
    });
  };

  // Fetch Google Courses
  const fetchGoogleCourses = async () => {
    setIsLoadingCourses(true);
    setError('');

    try {
      const response = await gradeApi.get({ path: '/api/v2/google-classroom/courses' });
      if (response && response.data && response.data.courses) {
        setGoogleCourses(response.data.courses);
      } else {
        setError('No courses found in your Google Classroom');
      }
    } catch (error) {
      console.error('Error fetching Google Classroom courses:', error);

      // Check if it's an authentication error
      const errorData = error.response?.data;
      if (
        errorData?.auth_required ||
        error.response?.status === 401 ||
        error.response?.status === 403
      ) {
        setError('Your Google Classroom session has expired. Please reconnect.');
        setGoogleAuthStatus({ authenticated: false, loading: false });
        setStep(1);
      } else {
        setError('Failed to fetch Google Classroom courses');
      }
    } finally {
      setIsLoadingCourses(false);
    }
  };

  // Fetch course assignments
  const fetchCourseAssignments = async courseId => {
    setIsLoadingAssignments(true);
    setError('');

    try {
      const response = await gradeApi.get({
        path: `/api/v2/google-classroom/courses/${courseId}/assignments`
      });
      if (response && response.data && response.data.assignments) {
        setCourseAssignments(response.data.assignments);
        setStep(3);
      } else {
        setError('No assignments found in this course');
      }
    } catch (error) {
      console.error('Error fetching course assignments:', error);

      // Check if it's an authentication error
      const errorData = error.response?.data;
      if (
        errorData?.auth_required ||
        error.response?.status === 401 ||
        error.response?.status === 403
      ) {
        setError('Your Google Classroom session has expired. Please reconnect.');
        setGoogleAuthStatus({ authenticated: false, loading: false });
        setStep(1);
      } else {
        setError('Failed to fetch course assignments');
      }
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  // Fetch assignment submissions
  const fetchAssignmentSubmissions = async (courseId, assignmentId) => {
    setIsLoadingSubmissions(true);
    setError('');

    try {
      const response = await gradeApi.get({
        path: `/api/v2/google-classroom/courses/${courseId}/assignments/${assignmentId}/submissions`
      });
      if (response && response.data && response.data.submissions) {
        setAssignmentSubmissions(response.data.submissions);
        setStep(4);
      } else {
        setError('No submissions found for this assignment');
      }
    } catch (error) {
      console.error('Error fetching assignment submissions:', error);

      // Check if it's an authentication error
      const errorData = error.response?.data;
      if (
        errorData?.auth_required ||
        error.response?.status === 401 ||
        error.response?.status === 403
      ) {
        setError('Your Google Classroom session has expired. Please reconnect.');
        setGoogleAuthStatus({ authenticated: false, loading: false });
        setStep(1);
      } else {
        setError('Failed to fetch assignment submissions');
      }
    } finally {
      setIsLoadingSubmissions(false);
    }
  };

  // Import submissions
  const importSubmissions = async () => {
    if (!selectedCourse || !selectedAssignment) {
      setError('Please select both a course and assignment');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      // First, get the submission files from Google Classroom
      const submissionsResponse = await gradeApi.get({
        path: `/api/v2/google-classroom/courses/${selectedCourse.id}/assignments/${selectedAssignment.id}/submissions`
      });

      if (
        !submissionsResponse.data.submissions ||
        submissionsResponse.data.submissions.length === 0
      ) {
        setError('No submissions found for this assignment');
        return;
      }

      // Download the files from Google Classroom
      toast.loading('Downloading submission files from Google Classroom...', {
        id: 'download-toast'
      });

      const downloadResponse = await gradeApi.post({
        path: '/api/v2/google-classroom/download-submission-files',
        body: {
          courseId: selectedCourse.id,
          assignmentId: selectedAssignment.id,
          submissions: submissionsResponse.data.submissions
        }
      });

      toast.dismiss('download-toast');

      if (!downloadResponse.data.success) {
        setError('Failed to download submission files from Google Classroom');
        return;
      }

      // Show download results
      if (downloadResponse.data.failed_count > 0) {
        toast.error(
          `Failed to download ${downloadResponse.data.failed_count} file(s) from Google Classroom`
        );
      }

      toast.success(
        `Downloaded ${downloadResponse.data.downloaded_count} file(s) from Google Classroom`
      );

      // Create FormData with the downloaded files (similar to how individual upload works)
      const formData = new FormData();
      const files = downloadResponse.data.files;

      // Add each downloaded file to FormData
      files.forEach((file, index) => {
        // Decode base64 content to binary
        const binaryString = atob(file.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], {
          type: file.contentType || 'application/pdf'
        });
        formData.append(`file_data_${index}`, blob, file.filename);

        // Add Google Classroom submission ID with course and assignment context for grade release
        const googleClassroomSubmissionId = `${selectedCourse.id}:${selectedAssignment.id}:${file.submissionId}`;
        formData.append(`google_classroom_submission_id_${index}`, googleClassroomSubmissionId);
      });

      // Set the required parameters for individual upload mode
      formData.append('upload_mode', 'individual');
      formData.append('file_count', files.length);
      formData.append('use_text_alignment', 'true');

      // Send to the existing batch-upload pipeline
      const uploadResponse = await gradeApi.post({
        path: `/api/assignment/${assignmentId}/batch-upload/`,
        body: formData,
        config: {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      });

      if (uploadResponse.status === 200) {
        toast.success(`Successfully imported ${files.length} submissions from Google Classroom`);

        // Check if there were any file processing issues
        if (uploadResponse.data.error_files > 0) {
          toast.error(`${uploadResponse.data.error_files} file(s) could not be processed`);
        }

        onImportComplete();
      } else {
        setError('Failed to process Google Classroom submissions');
      }
    } catch (error) {
      console.error('Error importing submissions:', error);

      // Check if it's an authentication error
      const errorData = error.response?.data;
      if (
        errorData?.auth_required ||
        error.response?.status === 401 ||
        error.response?.status === 403
      ) {
        setError('Your Google Classroom session has expired. Please reconnect.');
        setGoogleAuthStatus({ authenticated: false, loading: false });
        setStep(1);
      } else {
        setError(error.response?.data?.error || 'Failed to import submissions');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Logout from Google
  const handleGoogleLogout = async () => {
    try {
      await gradeApi.post({ path: '/api/v2/google-classroom/logout' });
      setGoogleAuthStatus({ authenticated: false, loading: false });
      setStep(1);
      setGoogleCourses([]);
      setSelectedCourse(null);
      setCourseAssignments([]);
      setSelectedAssignment(null);
      setAssignmentSubmissions([]);
    } catch (error) {
      console.error('Error logging out:', error);
      setError('Failed to disconnect from Google Classroom');
    }
  };

  // Initialize
  useEffect(() => {
    checkGoogleAuthStatus();
  }, []);

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="wizard-step">
            <h3>Connect to Google Classroom</h3>
            <p>First, you need to connect your Google Classroom account to import assignments.</p>

            {googleAuthStatus.loading ? (
              <div className="text-center py-4">
                <Loader className="animate-spin mx-auto mb-2" size={24} />
                <p>Checking authentication...</p>
              </div>
            ) : (
              <button
                className="btn btn-primary"
                disabled={isUploading}
                onClick={authenticateWithGoogle}
              >
                Connect to Google Classroom
              </button>
            )}
          </div>
        );

      case 2:
        return (
          <div className="wizard-step">
            <div className="flex justify-between items-center mb-4">
              <h3>Select Google Classroom Course</h3>
              <button className="btn btn-outline btn-sm" onClick={handleGoogleLogout}>
                <LogOut size={14} />
                Disconnect
              </button>
            </div>

            {isLoadingCourses ? (
              <div className="text-center py-4">
                <Loader className="animate-spin mx-auto mb-2" size={24} />
                <p>Loading courses...</p>
              </div>
            ) : googleCourses.length === 0 ? (
              <div className="text-center py-4">
                <p>No courses found in your Google Classroom account.</p>
                <button className="btn btn-outline mt-2" onClick={fetchGoogleCourses}>
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>
            ) : (
              <div className="courses-list">
                {googleCourses.map(course => (
                  <div
                    key={course.id}
                    className={`course-item ${selectedCourse?.id === course.id ? 'selected' : ''}`}
                    onClick={() => setSelectedCourse(course)}
                  >
                    <div className="course-info">
                      <h4>{course.name}</h4>
                      {course.section && (
                        <p className="text-sm text-gray-600">Section: {course.section}</p>
                      )}
                      {course.room && <p className="text-sm text-gray-600">Room: {course.room}</p>}
                    </div>
                    <ChevronRight size={16} />
                  </div>
                ))}
              </div>
            )}

            {selectedCourse && (
              <div className="mt-4">
                <button
                  className="btn btn-primary"
                  disabled={isLoadingAssignments}
                  onClick={() => fetchCourseAssignments(selectedCourse.id)}
                >
                  {isLoadingAssignments ? <Loader className="animate-spin mr-2" size={16} /> : null}
                  Continue to Assignments
                </button>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="wizard-step">
            <div className="flex justify-between items-center mb-4">
              <h3>Select Assignment</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setStep(2)}>
                <ArrowLeft size={14} />
                Back to Courses
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Course:</strong> {selectedCourse?.name}
              </p>
            </div>

            {isLoadingAssignments ? (
              <div className="text-center py-4">
                <Loader className="animate-spin mx-auto mb-2" size={24} />
                <p>Loading assignments...</p>
              </div>
            ) : courseAssignments.length === 0 ? (
              <div className="text-center py-4">
                <p>No assignments found in this course.</p>
                <button
                  className="btn btn-outline mt-2"
                  onClick={() => fetchCourseAssignments(selectedCourse.id)}
                >
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>
            ) : (
              <div className="assignments-list">
                {courseAssignments.map(assignment => (
                  <div
                    key={assignment.id}
                    className={`assignment-item ${selectedAssignment?.id === assignment.id ? 'selected' : ''}`}
                    onClick={() => setSelectedAssignment(assignment)}
                  >
                    <div className="assignment-info">
                      <h4>{assignment.title}</h4>
                      <p className="text-sm text-gray-600">{assignment.description}</p>
                      <div className="assignment-meta">
                        {assignment.maxPoints && (
                          <span className="meta-item">
                            <FileText size={12} />
                            {assignment.maxPoints} pts
                          </span>
                        )}
                        {/*{assignment.dueDate && (*/}
                        {/*  <span className="meta-item">*/}
                        {/*    <Calendar size={12} />*/}
                        {/*    Due: {new Date(assignment.dueDate).toLocaleDateString()}*/}
                        {/*  </span>*/}
                        {/*)}*/}
                      </div>
                    </div>
                    <ChevronRight size={16} />
                  </div>
                ))}
              </div>
            )}

            {selectedAssignment && (
              <div className="mt-4">
                <button
                  className="btn btn-primary"
                  disabled={isLoadingSubmissions}
                  onClick={() =>
                    fetchAssignmentSubmissions(selectedCourse.id, selectedAssignment.id)
                  }
                >
                  {isLoadingSubmissions ? <Loader className="animate-spin mr-2" size={16} /> : null}
                  Check Submissions
                </button>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="wizard-step">
            <div className="flex justify-between items-center mb-4">
              <h3>Import Submissions</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setStep(3)}>
                <ArrowLeft size={14} />
                Back to Assignments
              </button>
            </div>

            <div className="mb-4 space-y-2">
              <div className="p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Course:</strong> {selectedCourse?.name}
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-md">
                <p className="text-sm text-green-800">
                  <strong>Assignment:</strong> {selectedAssignment?.title}
                </p>
              </div>
            </div>

            {isLoadingSubmissions ? (
              <div className="text-center py-4">
                <Loader className="animate-spin mx-auto mb-2" size={24} />
                <p>Loading submissions...</p>
              </div>
            ) : (
              <div className="submissions-summary">
                <div className="flex items-center mb-4">
                  <Users size={20} className="mr-2" />
                  <span>
                    Found <strong>{assignmentSubmissions.length}</strong> total submissions
                  </span>
                </div>

                {(() => {
                  const submissionsWithAttachments = assignmentSubmissions.filter(
                    s => s.attachments && s.attachments.length > 0
                  );
                  const totalAttachments = submissionsWithAttachments.reduce(
                    (sum, s) => sum + s.attachments.length,
                    0
                  );

                  return (
                    <>
                      <div className="mb-4 p-3 bg-blue-50 rounded-md">
                        <p className="text-sm text-blue-800">
                          <strong>{submissionsWithAttachments.length}</strong> submissions have file
                          attachments ({totalAttachments} files total)
                        </p>
                        {assignmentSubmissions.length - submissionsWithAttachments.length > 0 && (
                          <p className="text-sm text-orange-600 mt-1">
                            <strong>
                              {assignmentSubmissions.length - submissionsWithAttachments.length}
                            </strong>{' '}
                            submissions without attachments will be skipped
                          </p>
                        )}
                      </div>

                      {submissionsWithAttachments.length > 0 ? (
                        <div className="submissions-preview">
                          <h4 className="text-sm font-medium mb-2">
                            Submissions with Attachments:
                          </h4>
                          <div className="submissions-list max-h-40 overflow-y-auto">
                            {submissionsWithAttachments.slice(0, 5).map((submission, index) => (
                              <div key={submission.id} className="submission-item">
                                <div className="flex items-center">
                                  <CheckCircle size={14} className="text-green-500 mr-2" />
                                  <span className="text-sm">
                                    Submission {index + 1} - {submission.attachments?.length || 0}{' '}
                                    file(s)
                                  </span>
                                </div>
                              </div>
                            ))}
                            {submissionsWithAttachments.length > 5 && (
                              <div className="text-sm text-gray-500 mt-2">
                                ... and {submissionsWithAttachments.length - 5} more submissions
                                with attachments
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <AlertTriangle size={24} className="text-yellow-500 mx-auto mb-2" />
                          <p>No submissions have file attachments to import</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {assignmentSubmissions && (
              <div className="mt-6">
                {(() => {
                  const submissionsWithAttachments = assignmentSubmissions.filter(
                    s => s.attachments && s.attachments.length > 0
                  );
                  const canImport = submissionsWithAttachments.length > 0;

                  return (
                    <button
                      type="button"
                      disabled={isUploading || !canImport}
                      className={`btn w-full ${
                        canImport && !isUploading ? 'btn-primary' : 'btn-secondary'
                      }`}
                      onClick={importSubmissions}
                    >
                      {isUploading ? (
                        <>
                          <Loader className="animate-spin mr-2" size={16} />
                          Importing Submissions...
                        </>
                      ) : (
                        <>
                          {canImport
                            ? `Import ${submissionsWithAttachments.length} Submission${submissionsWithAttachments.length !== 1 ? 's' : ''}`
                            : 'No Files to Import'}
                        </>
                      )}
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="google-classroom-wizard">
      <div className="wizard-header">
        <h2>Import from Google Classroom</h2>
        <div className="wizard-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Connect</div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Course</div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Assignment</div>
          <div className={`step ${step >= 4 ? 'active' : ''}`}>4. Import</div>
        </div>
      </div>

      <div className="wizard-content">
        {error && (
          <div className="error-message mb-4">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {renderStepContent()}
      </div>

      <div className="wizard-footer">
        <button className="btn btn-outline" disabled={isUploading} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default GoogleClassroomImportWizard;
